package diana.soleil.design.iraniancanadienbazzar.core.designsystem.component

import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.VisualTransformation
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme

/**
 * The single text input for the app. Tokens only; supports label, placeholder, error, leading/trailing
 * slots, single/multi line. Never hand-roll a text field — use this.
 */
@Composable
fun AppTextField(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    label: String? = null,
    placeholder: String? = null,
    enabled: Boolean = true,
    isError: Boolean = false,
    supportingText: String? = null,
    singleLine: Boolean = true,
    keyboardType: KeyboardType = KeyboardType.Text,
    visualTransformation: VisualTransformation = VisualTransformation.None,
    leading: @Composable (() -> Unit)? = null,
    trailing: @Composable (() -> Unit)? = null,
) {
    OutlinedTextField(
        value = value,
        onValueChange = onValueChange,
        modifier = modifier.fillMaxWidth(),
        enabled = enabled,
        isError = isError,
        singleLine = singleLine,
        label = label?.let { { Text(it, style = AppTheme.typography.labelMd) } },
        placeholder = placeholder?.let { { Text(it, style = AppTheme.typography.bodyMd, color = AppTheme.colors.onSurfaceVariant) } },
        supportingText = supportingText?.let { { Text(it, style = AppTheme.typography.labelSm) } },
        leadingIcon = leading,
        trailingIcon = trailing,
        textStyle = AppTheme.typography.bodyMd,
        shape = AppTheme.shapes.md,
        keyboardOptions = KeyboardOptions(keyboardType = keyboardType),
        visualTransformation = visualTransformation,
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = AppTheme.colors.primary,
            // outline, not outlineVariant: the variant is 1.7:1 on surface — an invisible field
            // boundary for low-vision users (WCAG 1.4.11 needs 3:1 for control boundaries).
            unfocusedBorderColor = AppTheme.colors.outline,
            errorBorderColor = AppTheme.colors.error,
            cursorColor = AppTheme.colors.primary,
            focusedContainerColor = AppTheme.colors.surface,
            unfocusedContainerColor = AppTheme.colors.surface,
        ),
    )
}

/**
 * Prominent search field. Pass a leading magnifier via [leading] slot (icon set TBD). When [onClick]
 * is provided the field is read-only and behaves as a button (navigates to a search screen).
 */
@Composable
fun AppSearchBar(
    value: String,
    onValueChange: (String) -> Unit,
    placeholder: String,
    modifier: Modifier = Modifier,
    leading: @Composable (() -> Unit)? = null,
    trailing: @Composable (() -> Unit)? = null,
) {
    OutlinedTextField(
        value = value,
        onValueChange = onValueChange,
        modifier = modifier.fillMaxWidth(),
        singleLine = true,
        placeholder = { Text(placeholder, style = AppTheme.typography.bodyMd, color = AppTheme.colors.onSurfaceVariant) },
        leadingIcon = leading,
        trailingIcon = trailing,
        textStyle = AppTheme.typography.bodyMd,
        shape = AppTheme.shapes.pill,
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = AppTheme.colors.primary,
            // outline, not outlineVariant: the variant is 1.7:1 on surface — an invisible field
            // boundary for low-vision users (WCAG 1.4.11 needs 3:1 for control boundaries).
            unfocusedBorderColor = AppTheme.colors.outline,
            focusedContainerColor = AppTheme.colors.surface,
            unfocusedContainerColor = AppTheme.colors.surface,
            cursorColor = AppTheme.colors.primary,
        ),
    )
}
